// frontend/app/components/lessonPlans/lessonPlanTheme.js
//
// Design tokens for the Lesson Plan workspace. Everything here layers on top of
// getMainContentColors(isDarkMode) — no new design system, per plan decision D2.

import { getMainContentColors } from "../MainContentArea";

/**
 * The seven steps of the Saras Learning Loop — Wonder → Explore → Understand →
 * Apply → Explain → Reflect → Grow. `key` matches
 * backend/services/lesson_phases.py LESSON_PHASES, so the rail is driven by
 * data, not by a hardcoded count — a lesson with four steps still renders, and
 * a v1 (five-phase) lesson arrives already normalized into this vocabulary.
 *
 * Accents: seven hues, each ≥3:1 against white AND against the dark panel
 * (#1e293b), so one hex carries both themes. None of them is an existing
 * semantic token (success / warning / info / danger / accentPrimary) or the
 * app-wide indigo — see the SARAS_BLUE note below for why that matters. The
 * two closest pairs (teal/cyan, purple/fuchsia) sit at non-adjacent steps, so
 * no two neighbouring pills read as the same colour.
 */
export const PHASE_META = {
  wonder: { label: "Wonder", caption: "Spark curiosity", icon: "head-question-outline", accent: "#ea580c" },
  explore: { label: "Explore", caption: "Investigate", icon: "cube-outline", accent: "#0d9488" },
  understand: { label: "Understand", caption: "Build the model", icon: "school-outline", accent: "#a855f7" },
  apply: { label: "Apply", caption: "Solve", icon: "lightning-bolt-outline", accent: "#ec4899" },
  explain: { label: "Explain", caption: "Make thinking visible", icon: "comment-text-outline", accent: "#0891b2" },
  reflect: { label: "Reflect", caption: "Look back", icon: "mirror", accent: "#d946ef" },
  grow: { label: "Grow", caption: "Mastery & next", icon: "trending-up", accent: "#65a30d" },
};

const FALLBACK_PHASE = {
  label: "Step",
  caption: "",
  icon: "circle-outline",
  accent: "#64748b",
};

export const getPhaseMeta = (phase) =>
  PHASE_META[String(phase || "").toLowerCase()] || FALLBACK_PHASE;

/**
 * The four teacher-facing sections, mirroring
 * backend/services/lesson_phases.py LOOP_SECTIONS. The SME review asked for
 * four named sections; the Loop's seven phases are what carries mastery
 * evidence, so the sections group the phases rather than replacing them.
 */
export const SECTION_META = {
  introduction: { label: "Introduction", phases: ["wonder"], icon: "flag-outline", accent: "#ea580c" },
  explore_and_explain: { label: "Explore and Explain", phases: ["explore", "understand"], icon: "cube-outline", accent: "#0d9488" },
  assessment_and_ensure: { label: "Assessment and Ensure", phases: ["apply", "explain"], icon: "lightning-bolt-outline", accent: "#ec4899" },
  summary: { label: "Summary", phases: ["reflect", "grow"], icon: "trending-up", accent: "#65a30d" },
};

export const SECTION_KEYS = Object.keys(SECTION_META);

const FALLBACK_SECTION = {
  label: "Section",
  phases: [],
  icon: "circle-outline",
  accent: "#64748b",
};

export const getSectionMeta = (section) =>
  SECTION_META[String(section || "")] || FALLBACK_SECTION;

/**
 * Derives a section key from a phase by finding which section's `phases`
 * list it belongs to. Used when a step arrives without `section` (an older
 * runtime payload) — see getStepSection below, which every call site uses.
 * Returns null for a phase that is not in any section, the same
 * silent-wrong-answer refusal `normalize_phase` makes on the backend.
 */
export const getSectionForPhase = (phase) => {
  const normalized = String(phase || "").toLowerCase();
  const key = SECTION_KEYS.find((candidate) =>
    SECTION_META[candidate].phases.includes(normalized)
  );
  return key || null;
};

/**
 * `step.section || getSectionForPhase(step.phase)`. Every call site uses
 * this, never `step.section` directly — an older runtime payload has no
 * `section` field and must still group correctly.
 */
export const getStepSection = (step) =>
  step?.section || getSectionForPhase(step?.phase);

/**
 * The four mastery bands from the Loop document, thresholds owned by
 * backend/services/mastery_engine.py MASTERY_BANDS. Only label and accent live
 * here — the student-facing band message has one source, and it is the backend.
 *
 * A band bar's *length* carries the score; the accent only carries identity, so
 * these are chosen for distinctness rather than as a heat ramp. Grey reads as
 * "we have barely seen you work yet", which is exactly what Discovering means.
 */
export const MASTERY_BAND_META = {
  discovering: { label: "Discovering", accent: "#64748b" },
  building: { label: "Building", accent: "#0891b2" },
  applying: { label: "Applying", accent: "#a855f7" },
  extending: { label: "Extending", accent: "#65a30d" },
};

const FALLBACK_BAND = { label: "Not started", accent: "#64748b" };

export const getMasteryBandMeta = (band) =>
  MASTERY_BAND_META[String(band || "").toLowerCase()] || FALLBACK_BAND;

/** Gradual-release badges from the storyboard. */
export const MODE_META = {
  I_DO: { label: "I DO", caption: "Teacher models", accent: "#3b82f6" },
  WE_DO: { label: "WE DO", caption: "Guided together", accent: "#8b5cf6" },
  YOU_DO: { label: "YOU DO", caption: "Independent", accent: "#10b981" },
};

export const getModeMeta = (mode) => MODE_META[String(mode || "").toUpperCase()] || null;

/** Alpha-blended fill for a solid accent, tuned per theme. */
export const tint = (hex, isDarkMode, strength = 1) => {
  const value = String(hex || "").replace("#", "");
  if (value.length !== 6) return "transparent";
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const alpha = (isDarkMode ? 0.18 : 0.1) * strength;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * All colors the Lesson Plan components need, in one object, so no component
 * reaches for a raw hex that has no dark-mode counterpart.
 */
// Saras blue, the brand colour the top bar is painted in. The app-wide
// `accentPrimary` is an indigo that reads purple, so the lesson surfaces
// overrode it here rather than in getMainContentColors — repainting every
// screen in the product is a bigger decision than this one.
//
// The dark value is not `info` (#60a5fa): an accent that matches an existing
// semantic token stops being able to mean anything on its own.
export const SARAS_BLUE = { light: "#0077b6", dark: "#38bdf8" };

export const getLessonColors = (isDarkMode) => {
  const base = getMainContentColors(isDarkMode);
  return {
    ...base,

    accentPrimary: isDarkMode ? SARAS_BLUE.dark : SARAS_BLUE.light,
    accentGlow: isDarkMode ? "rgba(56, 189, 248, 0.15)" : "rgba(0, 119, 182, 0.10)",
    pageBg: isDarkMode ? "#0f172a" : "#f8fafc",
    panelBg: isDarkMode ? "#1e293b" : "#ffffff",
    railBg: isDarkMode ? "#111c31" : "#ffffff",
    subtleBg: isDarkMode ? "rgba(148,163,184,0.08)" : "#f8fafc",

    // Elevation. The lesson surfaces are otherwise flat on purpose, so these
    // exist for the one card that has to read as lifted off the page rather
    // than drawn on it — the finder's box, which is `panelBg` white sitting on
    // `pageBg` #f8fafc. Two colours a shade apart, separated by a border at
    // 0.15 alpha, is not an edge; without a shadow the box dissolves.
    //
    // Navy rather than black in light mode: a grey shadow under a white card on
    // a cool-grey page reads as dirt, while a shadow tinted toward the page's
    // own hue reads as depth.
    shadow: isDarkMode ? "#000000" : "#0f172a",
    shadowOpacity: isDarkMode ? 0.5 : 0.1,

    // Focus ring. The finder's box wears `accentPrimary` at rest — full Saras
    // blue, not a muted version of it — because that weight is what makes it
    // read as the thing on the page. Focus therefore cannot announce itself by
    // changing the border colour; there is nothing darker to go to.
    //
    // So focus swaps the shadow instead: the neutral depth shadow becomes a
    // blue glow in the same hue as the edge. Nothing moves, no border width
    // changes, so the box does not shift by a pixel when a teacher clicks into
    // it — it lights up.
    focusRing: isDarkMode ? "rgba(56,189,248,0.55)" : "rgba(0,119,182,0.45)",

    success: isDarkMode ? "#34d399" : "#10b981",
    warning: isDarkMode ? "#fbbf24" : "#f59e0b",
    info: isDarkMode ? "#60a5fa" : "#3b82f6",
    danger: isDarkMode ? "#f87171" : "#ef4444",

    teacherTipBg: isDarkMode ? "rgba(251,191,36,0.14)" : "#fffbeb",
    teacherTipBorder: isDarkMode ? "rgba(251,191,36,0.4)" : "#fde68a",
    teacherTipText: isDarkMode ? "#fde68a" : "#92400e",

    // Derived from SARAS_BLUE, not from the indigo these used to sit on. The
    // label inside them is `accentPrimary`; when that moved to Saras blue and
    // these did not, the card became cyan text on an indigo wash.
    aiCardBg: isDarkMode ? "rgba(56,189,248,0.14)" : "#e6f4fb",
    aiCardBorder: isDarkMode ? "rgba(56,189,248,0.40)" : "#b6dff2",

    correctBg: isDarkMode ? "rgba(52,211,153,0.16)" : "#ecfdf5",
    correctBorder: isDarkMode ? "rgba(52,211,153,0.45)" : "#a7f3d0",
    incorrectBg: isDarkMode ? "rgba(248,113,113,0.16)" : "#fef2f2",
    incorrectBorder: isDarkMode ? "rgba(248,113,113,0.45)" : "#fecaca",
  };
};

/** Variant styling for `callout` blocks. */
export const getCalloutVariant = (variant, colors, isDarkMode) => {
  switch (String(variant || "info").toLowerCase()) {
    case "question":
      return {
        accent: colors.accentPrimary,
        // Tinted from the same hex `accent` resolves to, so the wash can never
        // drift away from the text sitting on it again.
        bg: tint(isDarkMode ? SARAS_BLUE.dark : SARAS_BLUE.light, isDarkMode, 1.4),
        border: isDarkMode ? "rgba(56,189,248,0.40)" : "#b6dff2",
        icon: "help-circle-outline",
      };
    case "warning":
      return {
        accent: colors.warning,
        bg: colors.teacherTipBg,
        border: colors.teacherTipBorder,
        icon: "alert-outline",
      };
    case "success":
      return {
        accent: colors.success,
        bg: colors.correctBg,
        border: colors.correctBorder,
        icon: "check-circle-outline",
      };
    default:
      return {
        accent: colors.info,
        bg: tint(isDarkMode ? "#60a5fa" : "#3b82f6", isDarkMode, 1.4),
        border: isDarkMode ? "rgba(96,165,250,0.4)" : "#bfdbfe",
        icon: "information-outline",
      };
  }
};
